/**
 * PublisherPerformanceReportPage — per-affiliate KPI rollup with drill-through to campaigns.
 *
 * Composes: PageHeader, Toolbar (from/to date pickers, group-by, compare toggle,
 * column visibility), KPI tiles (with ±% deltas when compare enabled),
 * Main table (sortable columns, inline emerald sparkline, row-expand drill panel).
 * Sparkline is a pure SVG polyline — no chart library dependency.
 */
import { useMemo, useState } from "react";
import { usePublisherDrill, usePublisherRollup } from "./hooks";
import type {
	DrillCampaignRow,
	GroupBy,
	KpiDeltas,
	KpiTotals,
	PublisherRow,
	SparkPoint,
} from "./api";

type MetricKey =
	| "clicks"
	| "conversions"
	| "cr"
	| "payout"
	| "revenue"
	| "profit"
	| "epc";

type ColumnKey = "affiliate_name" | MetricKey | "spark";

const ALL_COLUMNS: ColumnKey[] = [
	"affiliate_name",
	"clicks",
	"conversions",
	"cr",
	"payout",
	"revenue",
	"profit",
	"epc",
	"spark",
];

const COLUMN_LABEL: Record<ColumnKey, string> = {
	affiliate_name: "Affiliate",
	clicks: "Clicks",
	conversions: "Conversions",
	cr: "CR%",
	payout: "Payout",
	revenue: "Revenue",
	profit: "Profit",
	epc: "EPC",
	spark: "CVR Trend",
};

function todayIso(): string {
	const d = new Date();
	return d.toISOString().slice(0, 10);
}

function daysAgoIso(n: number): string {
	const d = new Date();
	d.setDate(d.getDate() - n);
	return d.toISOString().slice(0, 10);
}

function fmtInt(n: number): string {
	return n.toLocaleString();
}

function fmtPct(n: number): string {
	return `${n.toFixed(2)}%`;
}

function fmtCurrency(n: number): string {
	return `$${n.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
}

function fmtDelta(pct: number): { text: string; up: boolean | null } {
	if (pct === 0 || !Number.isFinite(pct)) return { text: "0.0%", up: null };
	const up = pct > 0;
	const sign = up ? "+" : "";
	return { text: `${sign}${pct.toFixed(1)}%`, up };
}

function Sparkline({ points }: { points: SparkPoint[] }) {
	if (!points || points.length === 0) {
		return (
			<span className="text-xs text-zinc-400" data-testid="spark-empty">
				—
			</span>
		);
	}
	const w = 60;
	const h = 24;
	const values = points.map((p) => p.cvr);
	const min = Math.min(...values);
	const max = Math.max(...values);
	const range = max - min || 1;
	const step = points.length > 1 ? w / (points.length - 1) : w;
	const pts = points
		.map((p, i) => {
			const x = i * step;
			const y = h - ((p.cvr - min) / range) * h;
			return `${x.toFixed(1)},${y.toFixed(1)}`;
		})
		.join(" ");
	return (
		<svg
			width={w}
			height={h}
			viewBox={`0 0 ${w} ${h}`}
			className="inline-block align-middle"
			role="img"
			aria-label="CVR trend"
			data-testid="spark-svg"
		>
			<polyline
				fill="none"
				stroke="#10b981"
				strokeWidth={1.5}
				strokeLinejoin="round"
				strokeLinecap="round"
				points={pts}
			/>
		</svg>
	);
}

function KpiTile({
	label,
	value,
	delta,
}: {
	label: string;
	value: string;
	delta?: { text: string; up: boolean | null };
}) {
	const deltaClass =
		delta === undefined
			? ""
			: delta.up === true
				? "text-emerald-600"
				: delta.up === false
					? "text-red-600"
					: "text-zinc-500";
	return (
		<div
			className="rounded-lg border border-zinc-200 border-l-4 border-l-emerald-500 bg-white p-5 shadow-sm"
			data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
		>
			<div className="text-xs font-medium uppercase tracking-wider text-zinc-500">
				{label}
			</div>
			<div className="mt-2 text-3xl font-semibold tabular-nums text-zinc-900">
				{value}
			</div>
			{delta ? (
				<div className={`mt-1 flex items-center gap-1 text-sm ${deltaClass}`}>
					{delta.up === true ? "\u2191" : delta.up === false ? "\u2193" : "\u2014"}{" "}
					{delta.text}
				</div>
			) : null}
		</div>
	);
}

function DrillPanel({
	affiliateId,
	from,
	to,
}: {
	affiliateId: string;
	from: string;
	to: string;
}) {
	const q = usePublisherDrill({ affiliateId, from, to }, true);
	if (q.isLoading) {
		return (
			<div
				className="px-6 py-4 text-sm text-zinc-500"
				data-testid="drill-loading"
			>
				Loading campaigns.
			</div>
		);
	}
	if (q.error) {
		return (
			<div className="px-6 py-4 text-sm text-red-600" role="alert">
				{q.error.message}
			</div>
		);
	}
	const rows: DrillCampaignRow[] = q.data?.campaigns ?? [];
	if (rows.length === 0) {
		return (
			<div
				className="px-6 py-4 text-sm text-zinc-500"
				data-testid="drill-empty"
			>
				No campaigns for this affiliate in range.
			</div>
		);
	}
	return (
		<div className="bg-zinc-50 px-6 py-4" data-testid="drill-panel">
			<div className="mb-2 text-xs font-medium uppercase tracking-wider text-zinc-500">
				Campaign breakdown
			</div>
			<table className="w-full border-collapse text-sm">
				<thead>
					<tr className="text-xs uppercase tracking-wider text-zinc-500">
						<th className="px-3 py-2 text-left font-medium">Campaign</th>
						<th className="px-3 py-2 text-right font-medium">Clicks</th>
						<th className="px-3 py-2 text-right font-medium">Conv.</th>
						<th className="px-3 py-2 text-right font-medium">CR%</th>
						<th className="px-3 py-2 text-right font-medium">Payout</th>
						<th className="px-3 py-2 text-right font-medium">Revenue</th>
						<th className="px-3 py-2 text-right font-medium">Profit</th>
						<th className="px-3 py-2 text-right font-medium">EPC</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((c) => (
						<tr
							key={c.campaign_id}
							data-testid={`drill-row-${c.campaign_id}`}
							className="border-t border-zinc-200"
						>
							<td className="px-3 py-2 text-left text-zinc-900">
								{c.campaign_name}
							</td>
							<td className="px-3 py-2 text-right tabular-nums">
								{fmtInt(c.clicks)}
							</td>
							<td className="px-3 py-2 text-right tabular-nums">
								{fmtInt(c.conversions)}
							</td>
							<td className="px-3 py-2 text-right tabular-nums">
								{fmtPct(c.cr)}
							</td>
							<td className="px-3 py-2 text-right tabular-nums">
								{fmtCurrency(c.payout)}
							</td>
							<td className="px-3 py-2 text-right tabular-nums">
								{fmtCurrency(c.revenue)}
							</td>
							<td className="px-3 py-2 text-right tabular-nums">
								{fmtCurrency(c.profit)}
							</td>
							<td className="px-3 py-2 text-right tabular-nums">
								{fmtCurrency(c.epc)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export function PublisherPerformanceReportPage() {
	const [from, setFrom] = useState<string>(daysAgoIso(30));
	const [to, setTo] = useState<string>(todayIso());
	const [groupBy, setGroupBy] = useState<GroupBy>("day");
	const [compare, setCompare] = useState<boolean>(false);
	const [sortKey, setSortKey] = useState<MetricKey | "affiliate_name">(
		"clicks",
	);
	const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
	const [expanded, setExpanded] = useState<string | null>(null);
	const [visible, setVisible] = useState<Record<ColumnKey, boolean>>(() => {
		const o = {} as Record<ColumnKey, boolean>;
		for (const c of ALL_COLUMNS) o[c] = true;
		return o;
	});
	const [showColsMenu, setShowColsMenu] = useState(false);

	const rollupQ = usePublisherRollup({ from, to, groupBy, compare });

	const totals: KpiTotals = rollupQ.data?.totals ?? {
		active_affiliates: 0,
		total_clicks: 0,
		total_conversions: 0,
		total_payout: 0,
		avg_cr: 0,
		avg_epc: 0,
	};
	const deltas: KpiDeltas | undefined = rollupQ.data?.deltas;
	const rows: PublisherRow[] = rollupQ.data?.rows ?? [];
	const compareMap = useMemo(() => {
		const m = new Map<string, Record<string, number>>();
		for (const c of rollupQ.data?.compare ?? []) {
			m.set(c.affiliate_id, c as unknown as Record<string, number>);
		}
		return m;
	}, [rollupQ.data?.compare]);

	const sorted = useMemo(() => {
		const copy = [...rows];
		copy.sort((a, b) => {
			const av = a[sortKey] as unknown;
			const bv = b[sortKey] as unknown;
			if (typeof av === "number" && typeof bv === "number") {
				return sortDir === "asc" ? av - bv : bv - av;
			}
			const as = String(av ?? "");
			const bs = String(bv ?? "");
			return sortDir === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
		});
		return copy;
	}, [rows, sortKey, sortDir]);

	function toggleSort(k: MetricKey | "affiliate_name") {
		if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
		else {
			setSortKey(k);
			setSortDir(k === "affiliate_name" ? "asc" : "desc");
		}
	}

	function toggleExpand(id: string) {
		setExpanded((cur) => (cur === id ? null : id));
	}

	function toggleColumn(c: ColumnKey) {
		setVisible((v) => ({ ...v, [c]: !v[c] }));
	}

	const visibleCols = ALL_COLUMNS.filter((c) => visible[c]);
	const expandColSpan = visibleCols.length + 1; // +1 for expand caret

	return (
		<main
			data-testid="publisher-performance-report-page"
			className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-6"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Report
				</p>
				<h1 className="bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-2xl font-semibold text-transparent">
					Publisher Performance
				</h1>
				<p className="text-sm text-zinc-500">
					Per-affiliate KPI rollup with drill-through to campaigns.
				</p>
			</header>

			{/* Toolbar */}
			<section
				className="flex flex-wrap items-end gap-3 rounded-lg border border-zinc-200 bg-white p-4 shadow-sm"
				aria-label="Report filters"
			>
				<div>
					<label
						htmlFor="ppr-from"
						className="block text-xs font-medium uppercase tracking-wider text-zinc-500"
					>
						From
					</label>
					<input
						id="ppr-from"
						type="date"
						value={from}
						onChange={(e) => setFrom(e.target.value)}
						className="mt-1 w-40 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
						data-testid="ppr-from"
					/>
				</div>
				<div>
					<label
						htmlFor="ppr-to"
						className="block text-xs font-medium uppercase tracking-wider text-zinc-500"
					>
						To
					</label>
					<input
						id="ppr-to"
						type="date"
						value={to}
						onChange={(e) => setTo(e.target.value)}
						className="mt-1 w-40 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
						data-testid="ppr-to"
					/>
				</div>
				<div>
					<label
						htmlFor="ppr-groupby"
						className="block text-xs font-medium uppercase tracking-wider text-zinc-500"
					>
						Group by
					</label>
					<select
						id="ppr-groupby"
						value={groupBy}
						onChange={(e) => setGroupBy(e.target.value as GroupBy)}
						className="mt-1 w-32 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
						data-testid="ppr-groupby"
					>
						<option value="day">Day</option>
						<option value="week">Week</option>
						<option value="month">Month</option>
					</select>
				</div>
				<label
					className="flex cursor-pointer items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700"
					data-testid="ppr-compare-toggle"
				>
					<input
						type="checkbox"
						checked={compare}
						onChange={(e) => setCompare(e.target.checked)}
						className="accent-emerald-500"
						aria-label="Compare to previous period"
					/>
					Compare to previous period
				</label>
				<div className="relative ml-auto">
					<button
						type="button"
						className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
						onClick={() => setShowColsMenu((s) => !s)}
						data-testid="ppr-cols-button"
						aria-haspopup="menu"
						aria-expanded={showColsMenu}
					>
						Columns
					</button>
					{showColsMenu ? (
						<div
							role="menu"
							className="absolute right-0 top-full z-10 mt-1 w-48 rounded-md border border-zinc-200 bg-white p-2 shadow-lg"
							data-testid="ppr-cols-menu"
						>
							{ALL_COLUMNS.map((c) => (
								<label
									key={c}
									className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm text-zinc-700 hover:bg-zinc-50"
								>
									<input
										type="checkbox"
										checked={visible[c]}
										onChange={() => toggleColumn(c)}
										className="accent-emerald-500"
									/>
									{COLUMN_LABEL[c]}
								</label>
							))}
						</div>
					) : null}
				</div>
			</section>

			{/* KPI Tiles */}
			<section
				className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-6"
				aria-label="KPIs"
			>
				<KpiTile
					label="Active affiliates"
					value={fmtInt(totals.active_affiliates)}
					delta={
						compare && deltas
							? fmtDelta(deltas.active_affiliates_pct)
							: undefined
					}
				/>
				<KpiTile
					label="Total clicks"
					value={fmtInt(totals.total_clicks)}
					delta={
						compare && deltas ? fmtDelta(deltas.total_clicks_pct) : undefined
					}
				/>
				<KpiTile
					label="Total conversions"
					value={fmtInt(totals.total_conversions)}
					delta={
						compare && deltas
							? fmtDelta(deltas.total_conversions_pct)
							: undefined
					}
				/>
				<KpiTile
					label="Total payout"
					value={fmtCurrency(totals.total_payout)}
					delta={
						compare && deltas ? fmtDelta(deltas.total_payout_pct) : undefined
					}
				/>
				<KpiTile
					label="Avg CR"
					value={fmtPct(totals.avg_cr)}
					delta={compare && deltas ? fmtDelta(deltas.avg_cr_pct) : undefined}
				/>
				<KpiTile
					label="Avg EPC"
					value={fmtCurrency(totals.avg_epc)}
					delta={compare && deltas ? fmtDelta(deltas.avg_epc_pct) : undefined}
				/>
			</section>

			{/* Main table */}
			<section
				className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm"
				aria-label="Affiliate performance table"
			>
				{rollupQ.isLoading ? (
					<p
						className="px-6 py-8 text-sm text-zinc-500"
						data-testid="publisher-performance-report-loading"
					>
						Loading.
					</p>
				) : rollupQ.error ? (
					<p
						className="px-6 py-8 text-sm text-red-600"
						role="alert"
					>
						{rollupQ.error.message}
					</p>
				) : sorted.length === 0 ? (
					<p
						className="px-6 py-8 text-sm text-zinc-500"
						data-testid="publisher-performance-report-empty"
					>
						No data for selected range.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse text-sm"
							data-testid="publisher-performance-report-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className="w-8 px-3 py-3" aria-label="Expand" />
									{visibleCols.map((c) => {
										const isSortable = c !== "spark";
										const isActive = sortKey === c;
										return (
											<th
												key={c}
												className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500"
											>
												{isSortable ? (
													<button
														type="button"
														onClick={() =>
															toggleSort(c as MetricKey | "affiliate_name")
														}
														className={`flex items-center gap-1 ${
															isActive
																? "text-emerald-700"
																: "text-zinc-500 hover:text-zinc-700"
														}`}
														data-testid={`ppr-sort-${c}`}
													>
														{COLUMN_LABEL[c]}
														{isActive
															? sortDir === "asc"
																? "\u2191"
																: "\u2193"
															: null}
													</button>
												) : (
													COLUMN_LABEL[c]
												)}
											</th>
										);
									})}
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200">
								{sorted.map((row) => {
									const isOpen = expanded === row.affiliate_id;
									const cmp = compareMap.get(row.affiliate_id);
									return (
										<>
											<tr
												key={row.affiliate_id}
												className="cursor-pointer transition-colors hover:bg-emerald-50/40"
												onClick={() => toggleExpand(row.affiliate_id)}
												data-testid={`publisher-performance-report-row-${row.affiliate_id}`}
											>
												<td className="px-3 py-3 text-zinc-500">
													<span aria-hidden>{isOpen ? "\u25BE" : "\u25B8"}</span>
													<span className="sr-only">
														{isOpen ? "Collapse" : "Expand"}
													</span>
												</td>
												{visibleCols.map((c) => {
													if (c === "affiliate_name") {
														return (
															<td
																key={c}
																className="px-3 py-3 text-zinc-900"
															>
																{row.affiliate_name}
															</td>
														);
													}
													if (c === "spark") {
														return (
															<td key={c} className="px-3 py-3">
																<Sparkline points={row.spark} />
															</td>
														);
													}
													const n = row[c as MetricKey] as number;
													const txt =
														c === "cr"
															? fmtPct(n)
															: c === "clicks" || c === "conversions"
																? fmtInt(n)
																: fmtCurrency(n);
													const deltaPct = cmp
														? (cmp[`${c}_delta_pct`] as number | undefined)
														: undefined;
													return (
														<td
															key={c}
															className="px-3 py-3 text-left tabular-nums text-zinc-900"
														>
															{txt}
															{compare &&
															typeof deltaPct === "number" &&
															deltaPct !== 0 ? (
																<span
																	className={`ml-2 text-xs ${
																		deltaPct > 0
																			? "text-emerald-600"
																			: "text-red-600"
																	}`}
																	data-testid={`ppr-cell-delta-${row.affiliate_id}-${c}`}
																>
																	{deltaPct > 0 ? "+" : ""}
																	{deltaPct.toFixed(1)}%
																</span>
															) : null}
														</td>
													);
												})}
											</tr>
											{isOpen ? (
												<tr
													key={`${row.affiliate_id}-drill`}
													data-testid={`drill-open-${row.affiliate_id}`}
												>
													<td colSpan={expandColSpan} className="p-0">
														<DrillPanel
															affiliateId={row.affiliate_id}
															from={from}
															to={to}
														/>
													</td>
												</tr>
											) : null}
										</>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default PublisherPerformanceReportPage;
